import { useState, useCallback, useRef, useEffect, useMemo } from "react";
// import { useState, useCallback, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { API, BASE_URL } from "../api";
// ─── CONFIG ───────────────────────────────────────────────────
// const API_BASE = "http://localhost:5000/api";
// const API_BASE = "http://192.168.0.57:5000/api";
const TAX_RATE  = 0.18;
const ADV_PCT   = 0.30;
const NIGHT_CHARGE = 250;

// ─── MOCK FALLBACKS ───────────────────────────────────────────
const mockExtras = [
  { id: 1, name: "GPS Navigation",     icon: "🗺️", price: 150, unit: "per day", desc: "Full India maps with live traffic",      isFree: false },
  { id: 2, name: "Child Seat",         icon: "👶", price: 200, unit: "per day", desc: "Certified safety seat for infants",       isFree: false },
  { id: 3, name: "Additional Driver",  icon: "👤", price: 300, unit: "total",   desc: "Add a second named driver",               isFree: false },
  { id: 4, name: "Full Insurance",     icon: "🛡️", price: 400, unit: "total",   desc: "Zero excess comprehensive cover",         isFree: false },
  { id: 5, name: "Roadside Assist",    icon: "🔧", price: 100, unit: "total",   desc: "24/7 on-road breakdown support",          isFree: false },
];

const mockInsurance = [
  { ins_id: 1, ins_item: "Basic Protection",    ins_chargeperday: 200, slab_prices: {} },
  { ins_id: 2, ins_item: "Comprehensive Cover", ins_chargeperday: 400, slab_prices: {} },
];

const mockBookingContext = {
  pickup: "Bhubaneswar Airport", dropoff: "Bhubaneswar Airport",
  pickupDate: "2025-05-10", pickupTime: "10:00",
  returnDate: "2025-05-13", returnTime: "10:00",
  driverAge: "26-35", days: 3,
  selectedCar: {
    car_id: 2, car_name: "Innova Crysta", car_brand: "Toyota",
    cat_name: "MPV", group_name: "7 Seater",
    car_passgrno: 7, car_luggno: 4,
    car_transmission: "Automatic", car_airconditionr: "Yes",
    car_image: "", base_price: 2800,
  },
  selectedExtras: [],
  selectedInsurance: null,
};

const COUNTRIES = [
  "India (+91)", "UK (+44)", "USA (+1)", "Australia (+61)",
  "Canada (+1)", "UAE (+971)", "Germany (+49)", "France (+33)",
  "Singapore (+65)", "Japan (+81)", "New Zealand (+64)", "South Africa (+27)",
];

// ─── CSS ──────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=DM+Sans:wght@300;400;500;600&display=swap');

  :root {
    --gold: #C9A84C; --gold-light: #E8C97A;
    --dark: #0A0A0F; --dark2: #111118; --dark3: #1A1A24;
    --surface: #16161F; --border: rgba(201,168,76,0.18);
    --text: #E8E8F0; --muted: #7A7A9A; --white: #FFFFFF;
    --green: #1D9E75; --red: #E24B4A; --amber: #D97706;
    --nav-h: 64px;
    --bar-h: 0px; /* updated via JS */
  }

  *, *::before, *::after { margin:0; padding:0; box-sizing:border-box; }
  html { scroll-behavior:smooth; -webkit-text-size-adjust:100%; }
  body { font-family:'DM Sans',sans-serif; background:var(--dark); color:var(--text); overflow-x:hidden; min-height:100dvh; }

  /* ── NAV ── */
  .nav {
    position:fixed; top:0; left:0; right:0; z-index:1000;
    display:flex; align-items:center; justify-content:space-between;
    padding:0 clamp(1rem, 4vw, 5%); height:var(--nav-h);
    background:rgba(10,10,15,0.96); backdrop-filter:blur(18px);
    border-bottom:1px solid var(--border);
    gap:.75rem;
  }
  .nav-logo {
    font-family:'Playfair Display',serif;
    font-size:clamp(1.2rem, 3vw, 1.6rem);
    color:var(--gold); cursor:pointer; white-space:nowrap; flex-shrink:0;
  }
  .nav-logo span { color:var(--white); }

  .nav-steps { display:flex; align-items:center; overflow-x:auto; -webkit-overflow-scrolling:touch; scrollbar-width:none; }
  .nav-steps::-webkit-scrollbar { display:none; }
  .nav-step {
    display:flex; align-items:center; gap:.35rem;
    padding:.28rem .65rem; font-size:.72rem; font-weight:600;
    letter-spacing:.5px; border-radius:20px; color:var(--muted); white-space:nowrap;
  }
  .nav-step.done   { color:var(--green); }
  .nav-step.active { color:var(--gold); background:rgba(201,168,76,.1); }
  .nav-step-num {
    width:18px; height:18px; border-radius:50%;
    display:flex; align-items:center; justify-content:center;
    font-size:.62rem; font-weight:700; border:1.5px solid currentColor; flex-shrink:0;
  }
  .nav-step-arrow { color:var(--muted); font-size:.68rem; padding:0 .1rem; }

  /* ── SUMMARY BAR ── */
  .summary-bar {
    position:sticky; top:var(--nav-h); z-index:900;
    background:var(--dark2); border-bottom:1px solid var(--border);
  }

  /* Desktop: full row */
  .sb-full {
    padding:.75rem clamp(1rem, 4vw, 5%);
    display:flex; align-items:center; gap:.9rem; flex-wrap:wrap;
  }
  .sb-item { display:flex; align-items:center; gap:.4rem; font-size:.8rem; min-width:0; }
  .sb-label { color:var(--muted); font-size:.68rem; letter-spacing:.8px; text-transform:uppercase; white-space:nowrap; }
  .sb-val   { color:var(--text); font-weight:500; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .sb-divider { width:1px; height:24px; background:var(--border); flex-shrink:0; }
  .sb-days {
    background:rgba(201,168,76,.12); border:1px solid var(--gold);
    color:var(--gold); font-size:.74rem; font-weight:700;
    padding:.24rem .7rem; border-radius:20px; white-space:nowrap; flex-shrink:0;
  }
  .sb-edit {
    margin-left:auto; background:transparent; color:var(--gold);
    border:1px solid var(--gold); font-size:.74rem; font-weight:600;
    padding:.3rem .85rem; border-radius:8px; cursor:pointer;
    transition:all .2s; white-space:nowrap; flex-shrink:0;
  }
  .sb-edit:hover { background:var(--gold); color:var(--dark); }

  /* Mobile collapsed strip */
  .sb-collapsed { display:none; }
  .sb-expand-panel {
    display:none;
  }
  @media (max-width:768px) {
    .sb-full { display:none; }

    .sb-collapsed {
      display:flex; align-items:center; justify-content:space-between;
      padding:.55rem clamp(.75rem,3vw,1rem); gap:.5rem;
      cursor:pointer; user-select:none;
    }
    .sb-collapsed-left {
      display:flex; align-items:center; gap:.5rem; min-width:0; flex:1;
    }
    .sb-collapsed-loc {
      font-size:.82rem; font-weight:600; color:var(--text);
      white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
    }
    .sb-collapsed-loc span { color:var(--muted); font-weight:400; font-size:.76rem; }
    .sb-collapsed-dates {
      font-size:.74rem; color:var(--muted); white-space:nowrap;
    }
    .sb-collapsed-right {
      display:flex; align-items:center; gap:.45rem; flex-shrink:0;
    }
    .sb-chevron {
      color:var(--gold); font-size:.72rem; transition:transform .25s; line-height:1;
    }
    .sb-chevron.open { transform:rotate(180deg); }

    /* Expandable panel */
    .sb-expand-panel {
      overflow:hidden;
      max-height:0;
      transition:max-height .3s ease, padding .3s ease;
      border-top:0px solid var(--border);
      display:block;
    }
    .sb-expand-panel.open {
      max-height:300px;
      border-top:1px solid var(--border);
    }
    .sb-expand-inner {
      padding:.65rem clamp(.75rem,3vw,1rem) .75rem;
      display:grid; grid-template-columns:1fr 1fr;
      gap:.55rem .75rem;
    }
    .sb-expand-item { display:flex; flex-direction:column; gap:.15rem; }
    .sb-expand-label { font-size:.63rem; letter-spacing:.8px; text-transform:uppercase; color:var(--muted); }
    .sb-expand-val   { font-size:.8rem; color:var(--text); font-weight:500; }
    .sb-expand-actions {
      grid-column:1 / -1;
      display:flex; gap:.5rem; margin-top:.3rem;
    }
    .sb-expand-edit {
      flex:1; background:transparent; color:var(--gold);
      border:1px solid var(--gold); font-size:.78rem; font-weight:600;
      padding:.45rem .75rem; border-radius:8px; cursor:pointer;
      transition:all .2s; text-align:center;
    }
    .sb-expand-edit:hover { background:var(--gold); color:var(--dark); }
  }

  /* ── PAGE WRAPPER ── */
  .step3-wrap {
    padding-top:var(--nav-h);
    display:flex; flex-direction:column; min-height:100dvh;
  }

  .step3-header {
    padding:clamp(1rem,3vw,2rem) clamp(1rem,4vw,5%) .75rem;
  }
  .step3-header h1 {
    font-family:'Playfair Display',serif;
    font-size:clamp(1.4rem,3.5vw,2rem); color:var(--white);
  }
  .step3-header p { font-size:.86rem; color:var(--muted); margin-top:.3rem; line-height:1.5; }

  /* ── LAYOUT ── */
  .step3-layout {
    display:grid;
    grid-template-columns:1fr 340px;
    flex:1;
    align-items:start;
  }

  /* ── LEFT COL ── */
  .left-col {
    padding:0 clamp(1rem,3vw,2.5rem) clamp(2rem,6vw,4rem);
    min-width:0;
  }

  /* ── SECTION CARD ── */
  .s-card {
    background:var(--dark2); border:1px solid var(--border);
    border-radius:16px; padding:clamp(1rem,2.5vw,1.5rem);
    margin-bottom:1.25rem;
  }
  .s-title {
    font-size:.67rem; letter-spacing:2.5px; text-transform:uppercase;
    color:var(--gold); font-weight:700; margin-bottom:1.1rem;
    display:flex; align-items:center; gap:.6rem;
  }
  .s-title::after { content:''; flex:1; height:1px; background:var(--border); }

  /* ── CAR SUMMARY ── */
  .car-summary {
    display:grid; grid-template-columns:180px 1fr;
    gap:1.1rem; align-items:center;
  }
  .car-thumb {
    height:110px; background:var(--dark3); border-radius:10px;
    display:flex; align-items:center; justify-content:center;
    font-size:3rem; overflow:hidden;
  }
  .car-thumb img { width:100%; height:100%; object-fit:cover; }
  .car-s-cat  { font-size:.68rem; letter-spacing:1.5px; text-transform:uppercase; color:var(--gold); font-weight:700; margin-bottom:.2rem; }
  .car-s-name { font-family:'Playfair Display',serif; font-size:clamp(1.1rem,2.5vw,1.4rem); color:var(--white); margin-bottom:.2rem; }
  .car-s-brand{ font-size:.8rem; color:var(--muted); margin-bottom:.6rem; }
  .car-tags   { display:flex; gap:.4rem; flex-wrap:wrap; }
  .car-tag    { font-size:.7rem; color:var(--muted); background:var(--dark3); padding:.18rem .5rem; border-radius:6px; }
  .car-price-row  { display:flex; align-items:baseline; gap:.35rem; margin-top:.7rem; flex-wrap:wrap; }
  .car-price-big  { font-family:'Playfair Display',serif; font-size:clamp(1.2rem,2.5vw,1.5rem); color:var(--gold); }
  .car-price-sub  { font-size:.74rem; color:var(--muted); }
  .car-price-total{ color:var(--gold); font-weight:600; }

  /* ── EXTRAS ── */
  .extra-row {
    display:grid;
    grid-template-columns:28px 1fr auto 34px;
    align-items:center; gap:.65rem;
    padding:.6rem 0; border-bottom:1px solid var(--border);
    cursor:pointer; transition:background .15s, padding .15s, margin .15s;
  }
  .extra-row:last-child { border-bottom:none; }
  .extra-row:hover { background:rgba(201,168,76,.04); margin:0 -.55rem; padding:.6rem .55rem; border-radius:8px; }
  .extra-icon  { font-size:1.15rem; text-align:center; }
  .extra-name  { font-size:.84rem; color:var(--text); font-weight:500; line-height:1.3; }
  .extra-desc  { font-size:.7rem; color:var(--muted); margin-top:.08rem; }
  .extra-price { font-size:.8rem; color:var(--gold); font-weight:600; text-align:right; white-space:nowrap; }
  .extra-price small { display:block; font-size:.66rem; color:var(--muted); font-weight:400; }
  .extra-free  { color:var(--green); font-weight:700; }

  .toggle-btn {
    width:34px; height:19px; border-radius:10px; border:none;
    cursor:pointer; position:relative; transition:background .2s; flex-shrink:0;
  }
  .toggle-btn.on  { background:var(--green); }
  .toggle-btn.off { background:#2a2a3a; }
  .toggle-btn::after {
    content:''; position:absolute; top:2.5px;
    width:14px; height:14px; border-radius:50%;
    background:#fff; transition:left .18s;
  }
  .toggle-btn.on::after  { left:17px; }
  .toggle-btn.off::after { left:2.5px; }

  /* ── INSURANCE ── */
  .ins-row {
    display:grid;
    grid-template-columns:28px 1fr auto 18px;
    align-items:flex-start; gap:.65rem;
    padding:.6rem 0; border-bottom:1px solid var(--border);
    cursor:pointer; transition:background .15s, padding .15s, margin .15s;
  }
  .ins-row:last-child { border-bottom:none; }
  .ins-row:hover { background:rgba(201,168,76,.04); margin:0 -.55rem; padding:.6rem .55rem; border-radius:8px; }
  .ins-radio {
    width:18px; height:18px; border-radius:50%;
    border:2px solid var(--border); flex-shrink:0;
    display:flex; align-items:center; justify-content:center;
    transition:all .2s; margin-top:.15rem;
  }
  .ins-radio.on { border-color:var(--gold); background:var(--gold); }
  .ins-radio.on::after { content:''; width:7px; height:7px; border-radius:50%; background:var(--dark); }
  .ins-price { font-size:.8rem; color:var(--gold); font-weight:600; text-align:right; white-space:nowrap; }
  .ins-price small { display:block; font-size:.66rem; color:var(--muted); font-weight:400; }

  /* ── FORM ── */
  .form-grid { display:grid; grid-template-columns:1fr 1fr; gap:.85rem; margin-bottom:.85rem; }
  .form-grid.cols3 { grid-template-columns:1fr 1fr 1fr; }
  .form-grid.full  { grid-template-columns:1fr; }
  .form-group { display:flex; flex-direction:column; gap:.35rem; }
  .form-label { font-size:.68rem; letter-spacing:.8px; text-transform:uppercase; color:var(--muted); font-weight:600; }

  .form-input, .form-select, .form-textarea {
    background:var(--dark3); border:1px solid var(--border); color:var(--text);
    font-family:'DM Sans',sans-serif; font-size:.875rem;
    padding:.6rem .88rem; border-radius:10px; outline:none;
    transition:border-color .2s; width:100%;
    -webkit-appearance:none;
  }
  .form-input:focus, .form-select:focus, .form-textarea:focus { border-color:var(--gold); }
  .form-input::placeholder, .form-textarea::placeholder { color:var(--muted); }

  .form-select {
    cursor:pointer;
    background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M5 6 0 0h10z' fill='%237A7A9A'/%3E%3C/svg%3E");
    background-repeat:no-repeat; background-position:right .88rem center;
    padding-right:2.2rem;
    background-color:var(--dark3);
  }

  .form-textarea { resize:vertical; min-height:88px; }

  .prev-booking-bar {
    display:flex; align-items:center; gap:.7rem; flex-wrap:wrap;
    background:var(--dark3); border:1px solid var(--border);
    border-radius:10px; padding:.6rem .9rem; margin-bottom:1.1rem;
  }
  .prev-chk-label {
    font-size:.82rem; color:var(--muted); cursor:pointer;
    display:flex; align-items:center; gap:.42rem; white-space:nowrap;
  }
  .prev-chk-label input[type=checkbox] { accent-color:var(--gold); width:15px; height:15px; flex-shrink:0; }
  .prev-num-input {
    flex:1; min-width:120px; background:transparent; border:none; color:var(--text);
    font-family:'DM Sans',sans-serif; font-size:.84rem; outline:none;
    border-left:1px solid var(--border); padding-left:.7rem;
  }
  .prev-num-input::placeholder { color:var(--muted); }
  .prev-num-input:disabled { opacity:.35; cursor:not-allowed; }

  .terms-check { display:flex; align-items:flex-start; gap:.6rem; margin-top:1rem; }
  .terms-check input[type=checkbox] {
    accent-color:var(--gold); width:15px; height:15px;
    margin-top:.2rem; flex-shrink:0; cursor:pointer;
  }
  .terms-check label { font-size:.82rem; color:var(--muted); line-height:1.5; cursor:pointer; }
  .terms-link { color:var(--gold); text-decoration:underline; cursor:pointer; }

  /* ── NIGHT NOTICE ── */
  .night-notice {
    background:rgba(239,159,39,.08); border:1px solid rgba(239,159,39,.3);
    border-radius:10px; padding:.65rem 1rem; font-size:.77rem; color:#EF9F27;
    display:flex; align-items:flex-start; gap:.5rem; margin-bottom:1.1rem;
  }

  /* ── RIGHT COL ── */
  .right-col {
    background:var(--dark2); border-left:1px solid var(--border);
    padding:1.5rem clamp(.9rem,2vw,1.5rem);
    display:flex; flex-direction:column; gap:1.1rem;
    position:sticky; top:var(--nav-h); height:calc(100dvh - var(--nav-h));
    overflow-y:auto;
  }
  .right-col::-webkit-scrollbar { width:4px; }
  .right-col::-webkit-scrollbar-thumb { background:var(--border); border-radius:2px; }

  .bp-section {
    background:var(--surface); border:1px solid var(--border);
    border-radius:14px; padding:1.1rem;
  }
  .bp-heading {
    font-size:.66rem; letter-spacing:2px; text-transform:uppercase;
    color:var(--gold); font-weight:700; margin-bottom:.9rem;
  }

  .price-row { display:flex; justify-content:space-between; align-items:center; padding:.35rem 0; gap:.5rem; }
  .price-label { font-size:.8rem; color:var(--muted); }
  .price-val   { font-size:.86rem; color:var(--text); font-weight:500; text-align:right; }
  .price-row.sub-total { border-top:1px solid var(--border); margin-top:.4rem; padding-top:.65rem; }
  .price-row.sub-total .price-label { color:var(--text); font-weight:600; }
  .price-row.sub-total .price-val   { font-family:'Playfair Display',serif; font-size:1.35rem; color:var(--gold); }
  .price-row.grand { border-top:1px solid var(--border); margin-top:.3rem; padding-top:.6rem; }
  .price-row.grand .price-label { color:var(--text); font-weight:700; font-size:.92rem; }
  .price-row.grand .price-val   { font-family:'Playfair Display',serif; font-size:1.45rem; color:var(--white); }

  .adv-box {
    background:rgba(201,168,76,.08); border:1px solid rgba(201,168,76,.25);
    border-radius:12px; padding:.9rem 1.1rem; text-align:center;
  }
  .adv-label  { font-size:.7rem; color:var(--muted); letter-spacing:1px; text-transform:uppercase; margin-bottom:.28rem; }
  .adv-amount { font-family:'Playfair Display',serif; font-size:1.7rem; color:var(--gold); }
  .adv-sub    { font-size:.7rem; color:var(--muted); margin-top:.12rem; }

  /* ── CTA STACK ── */
  .cta-stack { display:flex; flex-direction:column; gap:.6rem; }

  .btn-proceed {
    width:100%; background:var(--gold); color:var(--dark);
    font-family:'DM Sans',sans-serif; font-weight:700; font-size:.97rem;
    padding:.88rem; border:none; border-radius:12px; cursor:pointer;
    transition:all .25s; display:flex; align-items:center; justify-content:center; gap:.5rem;
    touch-action:manipulation;
  }
  .btn-proceed:hover:not(:disabled) { background:var(--gold-light); transform:translateY(-2px); box-shadow:0 12px 30px rgba(201,168,76,.3); }
  .btn-proceed:disabled { background:var(--border); color:var(--muted); transform:none; box-shadow:none; cursor:not-allowed; }

  .btn-pay-later {
    width:100%; background:transparent;
    border:1.5px solid rgba(217,119,6,.55); color:var(--amber);
    font-family:'DM Sans',sans-serif; font-weight:600; font-size:.88rem;
    padding:.78rem; border-radius:12px; cursor:pointer;
    transition:all .25s; display:flex; align-items:center; justify-content:center; gap:.5rem;
    position:relative; overflow:hidden; touch-action:manipulation;
  }
  .btn-pay-later::before {
    content:''; position:absolute; inset:0;
    background:rgba(217,119,6,.06); opacity:0; transition:opacity .2s;
  }
  .btn-pay-later:hover:not(:disabled)::before { opacity:1; }
  .btn-pay-later:hover:not(:disabled) { border-color:var(--amber); transform:translateY(-1px); }
  .btn-pay-later:disabled { opacity:.35; cursor:not-allowed; transform:none; }

  .pay-later-badge {
    font-size:.6rem; background:rgba(217,119,6,.18); color:var(--amber);
    border-radius:4px; padding:.12rem .38rem; font-weight:700; letter-spacing:.5px;
  }

  .paylater-info {
    background:rgba(217,119,6,.06); border:1px solid rgba(217,119,6,.2);
    border-radius:10px; padding:.6rem .9rem; font-size:.75rem; color:var(--amber);
    display:flex; align-items:flex-start; gap:.5rem;
  }
  .secure-note { font-size:.7rem; color:var(--muted); text-align:center; }

  /* ── SUBMITTING OVERLAY ── */
  .submitting-overlay {
    position:fixed; inset:0; z-index:9999;
    background:rgba(10,10,15,.88); backdrop-filter:blur(6px);
    display:flex; flex-direction:column; align-items:center; justify-content:center; gap:1rem;
  }
  .spinner {
    width:46px; height:46px; border:3px solid var(--border);
    border-top-color:var(--gold); border-radius:50%;
    animation:spin .75s linear infinite;
  }
  @keyframes spin { to { transform:rotate(360deg); } }
  .submitting-text { font-size:.88rem; color:var(--muted); letter-spacing:.5px; }

  /* ── SKELETON ── */
  @keyframes shimmer { 0%{background-position:-600px 0} 100%{background-position:600px 0} }
  .skeleton {
    background:linear-gradient(90deg,var(--surface) 25%,var(--dark3) 50%,var(--surface) 75%);
    background-size:600px 100%; animation:shimmer 1.3s infinite; border-radius:6px;
  }

  /* ═══════════════════════════════════════════
     RESPONSIVE BREAKPOINTS
  ═══════════════════════════════════════════ */

  /* ── ≤1100px: shrink sidebar ── */
  @media (max-width:1100px) {
    .step3-layout { grid-template-columns:1fr 300px; }
    .car-summary  { grid-template-columns:150px 1fr; }
  }

  /* ── ≤900px: stack layout ── */
  @media (max-width:900px) {
    :root { --nav-h: 60px; }

    .step3-layout {
      display:flex; flex-direction:column;
    }

    /* right panel becomes inline, not sticky */
    .right-col {
      position:relative; top:0;
      height:auto; overflow:visible;
      border-left:none; border-top:1px solid var(--border);
      /* extra bottom space for fixed CTA bar */
      /* padding-bottom:calc(env(safe-area-inset-bottom) + 7rem);*/
      padding-bottom: 8rem;
      width:100%;
    }

    .left-col {
      padding-bottom:1rem;
      /* extra space so fixed CTA bar doesn't overlap */
      /* padding-bottom:calc(env(safe-area-inset-bottom) + 7rem); */
    }

    /* ── Fixed CTA bar at bottom ── */
    .cta-stack {
      position:fixed;
      bottom:0; left:0; right:0;
      background:var(--dark2);
      border-top:1px solid var(--border);
      padding:.75rem clamp(1rem,4vw,5%);
      padding-bottom:calc(env(safe-area-inset-bottom) + .75rem);
      z-index:950;
      flex-direction:row; gap:.6rem;
      box-shadow:0 -8px 32px rgba(0,0,0,.5);
    }
    .cta-stack .btn-proceed,
    .cta-stack .btn-pay-later { flex:1; padding:.75rem .5rem; font-size:.84rem; }
    .cta-stack .paylater-info { display:none; }
    .cta-stack .secure-note   { display:none; }

    /* show paylater info inside right-col on tablet */
    .paylater-info-inline { display:flex !important; }
    
  }

  /* ── ≤768px: tablet/large mobile ── */
  @media (max-width:768px) {
    .nav-steps { display:none; }

    .car-summary { grid-template-columns:1fr; }
    .car-thumb   { height:160px; width:100%; }

    .form-grid       { grid-template-columns:1fr; }
    .form-grid.cols3 { grid-template-columns:1fr; }
  }

  /* ── ≤480px: small phones ── */
  @media (max-width:480px) {
    :root { --nav-h:56px; }

    .nav-logo { font-size:1.15rem; }

    .step3-header h1 { font-size:1.3rem; }
    .step3-header p  { font-size:.78rem; }

    .s-card { padding:.9rem; border-radius:12px; }

    .summary-bar { padding:.6rem 1rem; }
    .sb-item { min-width:100%; }
    .sb-val  { font-size:.82rem; }

    .extra-row,
    .ins-row {
      grid-template-columns:24px 1fr 34px;
      row-gap:.15rem;
    }
    /* price wraps below name on tiny screens */
    .extra-price { grid-column:2; grid-row:2; text-align:left; font-size:.75rem; }
    .ins-price   { grid-column:2; grid-row:2; text-align:left; font-size:.75rem; }
    .toggle-btn  { grid-column:3; grid-row:1 / span 2; align-self:center; }
    .ins-radio   { grid-column:3; grid-row:1 / span 2; align-self:center; }

    .extra-row { align-items:start; }

    .car-price-big { font-size:1.2rem; }

    .btn-proceed,
    .btn-pay-later { font-size:.82rem; padding:.68rem .4rem; }

    .pay-later-badge { display:none; }

    .adv-amount { font-size:1.4rem; }
    .price-row.grand .price-val { font-size:1.25rem; }
  }

  /* ── ≤360px: very small phones ── */
  @media (max-width:360px) {
    .nav-steps { display:none; }
    .s-card { padding:.75rem; border-radius:10px; }
    .car-s-name { font-size:1rem; }
    .cta-stack { flex-direction:column; }
    .cta-stack .btn-proceed,
    .cta-stack .btn-pay-later { width:100%; }
  }

  /* ── Landscape small phones ── */
  @media (max-height:500px) and (max-width:900px) {
    .right-col { padding-bottom:5rem; }
    .left-col  { padding-bottom:5rem; }
  }

  /* ── Touch: remove hover transform on mobile ── */
  @media (hover:none) {
    .btn-proceed:hover:not(:disabled),
    .btn-pay-later:hover:not(:disabled) { transform:none; }
    .extra-row:hover { margin:0; padding:.6rem 0; background:transparent; border-radius:0; }
    .ins-row:hover   { margin:0; padding:.6rem 0; background:transparent; border-radius:0; }
  }
`;

// ─── VALIDATION RULES ──────────────────────────────────────────
const VALIDATORS = {
  "First Name": {
    required: true,
    minLength: 2,
    pattern: /^[a-zA-Z\s'-]+$/,
    messages: {
      required: "First name is required",
      minLength: "Must be at least 2 characters",
      pattern: "Only letters, spaces, hyphens and apostrophes allowed",
    },
  },
  "Last Name": {
    required: true,
    minLength: 2,
    pattern: /^[a-zA-Z\s'-]+$/,
    messages: {
      required: "Last name is required",
      minLength: "Must be at least 2 characters",
      pattern: "Only letters, spaces, hyphens and apostrophes allowed",
    },
  },
  "Mobile No": {
    required: true,
    pattern: /^[\+]?[\d\s\-\(\)]{7,15}$/,
    messages: {
      required: "Mobile number is required",
      pattern: "Enter a valid phone number (7–15 digits)",
    },
  },
  Email: {
    required: true,
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    messages: {
      required: "Email address is required",
      pattern: "Enter a valid email address",
    },
  },
  "Confirm Email": {
    required: true,
    matchField: "Email",
    messages: {
      required: "Please confirm your email",
      matchField: "Emails do not match",
    },
  },
  Address: {
    required: true,
    minLength: 5,
    messages: {
      required: "Address is required",
      minLength: "Please enter a full address",
    },
  },
  City: {
    required: true,
    minLength: 2,
    messages: {
      required: "City is required",
      minLength: "Enter a valid city name",
    },
  },
  "Post Code": {
    required: true,
    pattern: /^[a-zA-Z0-9\s\-]{3,10}$/,
    messages: {
      required: "Post / PIN code is required",
      pattern: "Enter a valid post / PIN code",
    },
  },
  "DL Number": {
    required: true,
    minLength: 5,
    messages: {
      required: "Driving licence number is required",
      minLength: "Enter a valid licence number",
    },
  },
  "DL Expiry": {
    required: true,
    futureDate: true,
    messages: {
      required: "Licence expiry date is required",
      futureDate: "Licence must not be expired",
    },
  },
};

function validateField(name, value, form = {}) {
  const rule = VALIDATORS[name];
  if (!rule) return null;

  const v = (value || "").trim();

  if (rule.required && !v) return rule.messages.required;
  if (!v) return null; // optional field — skip further checks

  if (rule.minLength && v.length < rule.minLength) return rule.messages.minLength;
  if (rule.pattern && !rule.pattern.test(v)) return rule.messages.pattern;
  if (rule.matchField && v !== (form[rule.matchField] || "").trim()) return rule.messages.matchField;
  if (rule.futureDate) {
    const expiry = new Date(v);
    const today  = new Date();
    today.setHours(0, 0, 0, 0);
    if (isNaN(expiry.getTime()) || expiry < today) return rule.messages.futureDate;
  }
  return null;
}

function validateAllFields(form) {
  const errors = {};
  let firstError = null;
  for (const name of Object.keys(VALIDATORS)) {
    const err = validateField(name, form[name], form);
    if (err) {
      errors[name] = err;
      if (!firstError) firstError = { name, err };
    }
  }
  return { errors, firstError, isValid: Object.keys(errors).length === 0 };
}

// ─── TOAST SYSTEM ──────────────────────────────────────────────
// Add this CSS inside the existing `css` template-literal (or inject via a <style> tag)
export const validationCSS = `

  /* ── TOAST CONTAINER ── */
  .toast-container {
    position: fixed;
    top: calc(var(--nav-h, 64px) + 1rem);
    right: 1.25rem;
    z-index: 10000;
    display: flex;
    flex-direction: column;
    gap: .55rem;
    pointer-events: none;
    max-width: min(400px, calc(100vw - 2.5rem));
  }

  /* ── SINGLE TOAST ── */
  .toast {
    pointer-events: all;
    display: flex;
    align-items: flex-start;
    gap: .65rem;
    padding: .75rem 1rem;
    border-radius: 14px;
    border: 1px solid transparent;
    backdrop-filter: blur(16px);
    font-family: 'DM Sans', sans-serif;
    font-size: .82rem;
    line-height: 1.45;
    box-shadow: 0 8px 32px rgba(0,0,0,.45);
    animation: toast-in .3s cubic-bezier(0.34, 1.56, 0.64, 1) both;
    transition: opacity .25s, transform .25s;
  }
  .toast.removing {
    animation: toast-out .25s ease forwards;
  }

  @keyframes toast-in {
    from { opacity: 0; transform: translateX(110%) scale(.92); }
    to   { opacity: 1; transform: translateX(0)   scale(1);    }
  }
  @keyframes toast-out {
    from { opacity: 1; transform: translateX(0)   scale(1);    }
    to   { opacity: 0; transform: translateX(110%) scale(.92); }
  }

  /* ── TOAST VARIANTS ── */
  .toast-error {
    background: rgba(20, 10, 10, 0.92);
    border-color: rgba(226, 75, 74, 0.45);
    color: #F5A0A0;
  }
  .toast-success {
    background: rgba(8, 22, 16, 0.92);
    border-color: rgba(29, 158, 117, 0.45);
    color: #6DD9B4;
  }
  .toast-warning {
    background: rgba(20, 14, 4, 0.92);
    border-color: rgba(217, 119, 6, 0.45);
    color: #FACC6C;
  }
  .toast-info {
    background: rgba(8, 12, 22, 0.92);
    border-color: rgba(201, 168, 76, 0.35);
    color: #C9A84C;
  }

  .toast-icon { font-size: 1.05rem; flex-shrink: 0; margin-top: .05rem; }
  .toast-body { flex: 1; }
  .toast-title {
    font-weight: 700;
    font-size: .84rem;
    margin-bottom: .12rem;
    color: var(--white, #fff);
  }
  .toast-msg { opacity: .85; }

  .toast-close {
    background: none; border: none; color: inherit;
    cursor: pointer; font-size: .95rem; opacity: .5;
    padding: 0; line-height: 1; flex-shrink: 0;
    transition: opacity .15s;
  }
  .toast-close:hover { opacity: 1; }

  /* ── FIELD VALIDATION STATES ── */
  .form-input.field-error,
  .form-select.field-error,
  .form-textarea.field-error {
    border-color: #E24B4A !important;
    background: rgba(226, 75, 74, 0.05);
    animation: field-shake .35s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
  }
  .form-input.field-ok,
  .form-select.field-ok {
    border-color: #1D9E75 !important;
    background: rgba(29, 158, 117, 0.05);
  }

  @keyframes field-shake {
    10%, 90% { transform: translateX(-2px); }
    20%, 80% { transform: translateX( 3px); }
    30%, 50%, 70% { transform: translateX(-3px); }
    40%, 60% { transform: translateX( 3px); }
  }

  .field-error-msg {
    font-size: .68rem;
    color: #E24B4A;
    margin-top: .28rem;
    display: flex;
    align-items: center;
    gap: .3rem;
    animation: fade-in .2s ease;
  }
  @keyframes fade-in { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; } }
  .field-error-msg::before { content: '⚠'; font-size: .7rem; }

  /* ── SUMMARY ERROR BANNER ── */
  .validation-summary {
    background: rgba(226, 75, 74, 0.08);
    border: 1px solid rgba(226, 75, 74, 0.35);
    border-radius: 12px;
    padding: .75rem 1rem;
    margin-bottom: 1.1rem;
    font-size: .8rem;
    color: #F5A0A0;
    display: flex;
    align-items: flex-start;
    gap: .6rem;
    animation: fade-in .25s ease;
  }
  .validation-summary strong { color: #E24B4A; display: block; margin-bottom: .25rem; font-size: .83rem; }
  .val-sum-icon { font-size: 1.1rem; flex-shrink: 0; margin-top: .05rem; }

  .nav-steps-container{
    width:100%;
    display:flex;
    justify-content:center;
    margin-top:20px;
  }
  
  .nav-steps{
    display:flex;
    align-items:center;
    flex-wrap:wrap;
    gap:10px;
    font-size:1rem;
    margin-top:80px;
  }
  .step3-wrap{
    padding-top:25px;
  }
`;


// ─── HELPERS ──────────────────────────────────────────────────
// const API_IMG = path =>
//   path && path !== "" && path !== "default.jpg"
//     ? `http://localhost:5000/${path}`
//     : null;
// const API_IMG = path =>
//   path && path !== "" && path !== "default.jpg"
//     ? `${API_BASE}/${path}`
//     : null;
const API_IMG = path =>
  path && path !== "" && path !== "default.jpg"
    ? `${BASE_URL}/${path}`
    : null;

const fmtPrice = n =>
  n === undefined || n === null || isNaN(n)
    ? "₹0"
    : "₹" + Math.round(Number(n)).toLocaleString("en-IN");

const isNightTime = t => { const h = parseInt(t?.split(":")[0] ?? "0"); return h >= 22 || h < 7; };

function getExtraIcon(name = "") {
  const n = name.toLowerCase();
  if (n.includes("gps") || n.includes("nav"))     return "🗺️";
  if (n.includes("child") || n.includes("baby"))  return "👶";
  if (n.includes("driver"))                        return "👤";
  if (n.includes("insur"))                         return "🛡️";
  if (n.includes("road") || n.includes("assist")) return "🔧";
  if (n.includes("wifi"))                          return "📶";
  if (n.includes("fuel"))                          return "⛽";
  return "➕";
}

function mapExtras(raw) {
  let src = Array.isArray(raw?.data) ? raw.data : Array.isArray(raw) ? raw : mockExtras;
  return src.map(e => {
    if (e.optdet_id !== undefined) {
      const rt = (e.rate_type || "").toLowerCase().replace(/\s+/g, "");
      const isPerDay = rt === "/day" || rt === "perday" || rt === "day" || rt === "daily";
      const isFree   = parseInt(e.opt_type) === 1;
      return {
        id:    e.optdet_id, name: e.optitem_name, icon: getExtraIcon(e.optitem_name),
        price: isFree ? 0 : parseFloat(e.opt_dayrate) || 0,
        unit:  isPerDay ? "per day" : "total",
        desc:  isFree ? "Included free with your rental"
                     : `Max ${e.opt_max} · Min ${e.min_bkngdys} booking day(s)`,
        isFree, selected: false,
      };
    }
    return { ...e, isFree: e.isFree ?? false, selected: false };
  });
}

function mapInsurance(raw) {
  if (Array.isArray(raw?.data)) return raw.data;
  if (Array.isArray(raw))       return raw;
  return mockInsurance;
}

// function resolveInsurancePrice(ins, days, daySlabs) {
//   if (!daySlabs?.length) return parseFloat(ins.ins_chargeperday || 0) * days;
//   const slab = daySlabs.find(s => days >= parseInt(s.FromIns_slab) && days <= parseInt(s.ToIns_slab));
//   if (slab && ins.slab_prices?.[slab.Insuranceslabid] !== undefined)
//     return parseFloat(ins.slab_prices[slab.Insuranceslabid]) || 0;
//   return parseFloat(ins.ins_chargeperday || 0) * days;
// }

// function totalInsurancePrice(selectedInsurances, days, daySlabs) {
//   return (selectedInsurances || []).reduce(
//     (sum, ins) => sum + resolveInsurancePrice(ins, days, daySlabs), 0
//   );
// }

function resolveInsurancePrice(ins, days) {

  const dailyPrice = parseFloat(ins.ins_chargeperday || 0);

  return dailyPrice * Number(days || 1);

}

function totalInsurancePrice(selectedInsurances, days) {

  return (selectedInsurances || []).reduce(
    (sum, ins) => sum + resolveInsurancePrice(ins, days),
    0
  );

}

// ─── FETCH HOOK ───────────────────────────────────────────────
function useFetch(endpoint, fallback) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    // fetch(`${API_BASE}${endpoint}`)
    //   .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
    //   .then(d => { setData(d); setLoading(false); })
    //   .catch(() => { setData(fallback); setLoading(false); });
    API.get(endpoint)
  .then(r => { setData(r.data); setLoading(false); })
  .catch(() => { setData(fallback); setLoading(false); });
  }, [endpoint]);
  return { data, loading };
}


// ─── NAV ──────────────────────────────────────────────────────
const STEPS = ["Location", "Choose Car", "Reserve", "Payment", "Confirm"];

function StepNav({ siteName, onLogoClick }) {
  const [settings, setSettings] = useState({
    site_name: "-Fleet",
    currency: "₹",
  });

  return (
    <>
      <Navbar
        activePage="Fleet"
        siteName={settings?.site_name}
      />

      <div className="nav-steps-container">
        <div className="nav-steps">
          {STEPS.map((s, i) => (
            <div
              key={s}
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <div
                className={`nav-step ${
                  i < 2
                    ? "done"
                    : i === 2
                    ? "active"
                    : ""
                }`}
              >
                <div className="nav-step-num">
                  {i < 2 ? "✓" : i + 1}
                </div>

                <span>{s}</span>
              </div>

              {i < STEPS.length - 1 && (
                <div className="nav-step-arrow">›</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ─── SUMMARY BAR ─────────────────────────────────────────────
function SummaryBar({ ctx, onEdit }) {
  const [open, setOpen] = useState(false);

  // Compact single-line label for mobile
  const shortLoc = ctx.pickup?.split(" ").slice(0, 2).join(" ") || "Pickup";
  const shortDate = ctx.pickupDate ? ctx.pickupDate.slice(5).replace("-", "/") : "";
  const shortRet  = ctx.returnDate  ? ctx.returnDate.slice(5).replace("-", "/"): "";

  return (
    <div className="summary-bar">

      {/* ── Desktop: full row ── */}
      <div className="sb-full">
        <div className="sb-item">
          <span>📍</span>
          <div>
            <div className="sb-label">Pickup</div>
            <div className="sb-val">{ctx.pickup}</div>
          </div>
        </div>
        <div className="sb-divider" />
        <div className="sb-item">
          <span>🏁</span>
          <div>
            <div className="sb-label">Drop-off</div>
            <div className="sb-val">{ctx.dropoff || ctx.pickup}</div>
          </div>
        </div>
        <div className="sb-divider" />
        <div className="sb-item">
          <span>📅</span>
          <div>
            <div className="sb-label">From</div>
            <div className="sb-val">{ctx.pickupDate} {ctx.pickupTime}</div>
          </div>
        </div>
        <div className="sb-divider" />
        <div className="sb-item">
          <span>📅</span>
          <div>
            <div className="sb-label">To</div>
            <div className="sb-val">{ctx.returnDate} {ctx.returnTime}</div>
          </div>
        </div>
        <div className="sb-divider" />
        <div className="sb-days">{ctx.days} day{ctx.days !== 1 ? "s" : ""}</div>
        <button className="sb-edit" onClick={onEdit}>✏ Edit Search</button>
      </div>

      {/* ── Mobile: collapsed strip ── */}
      <div className="sb-collapsed" onClick={() => setOpen(o => !o)}>
        <div className="sb-collapsed-left">
          <span>📍</span>
          <div>
            <div className="sb-collapsed-loc">
              {shortLoc} <span>· {ctx.days}d</span>
            </div>
            <div className="sb-collapsed-dates">
              {shortDate} {ctx.pickupTime} → {shortRet} {ctx.returnTime}
            </div>
          </div>
        </div>
        <div className="sb-collapsed-right">
          <div className="sb-days" style={{ margin:0 }}>{ctx.days}d</div>
          <div className={`sb-chevron ${open ? "open" : ""}`}>▼</div>
        </div>
      </div>

      {/* ── Mobile: expandable panel ── */}
      <div className={`sb-expand-panel ${open ? "open" : ""}`}>
        <div className="sb-expand-inner">
          <div className="sb-expand-item">
            <div className="sb-expand-label">📍 Pickup</div>
            <div className="sb-expand-val">{ctx.pickup}</div>
          </div>
          <div className="sb-expand-item">
            <div className="sb-expand-label">🏁 Drop-off</div>
            <div className="sb-expand-val">{ctx.dropoff || ctx.pickup}</div>
          </div>
          <div className="sb-expand-item">
            <div className="sb-expand-label">📅 From</div>
            <div className="sb-expand-val">{ctx.pickupDate} · {ctx.pickupTime}</div>
          </div>
          <div className="sb-expand-item">
            <div className="sb-expand-label">📅 To</div>
            <div className="sb-expand-val">{ctx.returnDate} · {ctx.returnTime}</div>
          </div>
          {ctx.driverAge && (
            <div className="sb-expand-item">
              <div className="sb-expand-label">👤 Driver Age</div>
              <div className="sb-expand-val">{ctx.driverAge}</div>
            </div>
          )}
          <div className="sb-expand-actions">
            <button className="sb-expand-edit" onClick={onEdit}>✏ Edit Search</button>
          </div>
        </div>
      </div>

    </div>
  );
}

// ─── CAR SUMMARY CARD ─────────────────────────────────────────
const CAR_EMOJI = { "Small Car":"🚗", "SUV":"🚙", "7 Seater":"🚐", "Luxury":"🏎️" };

function CarSummaryCard({ car, days }) {
  const img   = API_IMG(car?.car_image);
  const daily = car?.base_price || 0;
  const total = daily * days;

  return (
    <div className="s-card">
      <div className="s-title">Selected Vehicle</div>
      <div className="car-summary">
        <div className="car-thumb">
          {img ? <img src={img} alt={car?.car_name} /> : <span>{CAR_EMOJI[car?.group_name] || "🚘"}</span>}
        </div>
        <div>
          <div className="car-s-cat">{car?.cat_name}</div>
          <div className="car-s-name">{car?.car_name}</div>
          <div className="car-s-brand">{car?.car_brand} · {car?.group_name}</div>
          <div className="car-tags">
            {car?.car_passgrno && <span className="car-tag">{car.car_passgrno} seats</span>}
            {car?.car_luggno   && <span className="car-tag">{car.car_luggno} bags</span>}
            {car?.car_transmission && <span className="car-tag">{car.car_transmission}</span>}
            {car?.car_airconditionr === "Yes" && <span className="car-tag">AC</span>}
          </div>
          <div className="car-price-row">
            <span className="car-price-big">{fmtPrice(daily)}</span>
            <span className="car-price-sub">
              /day · {days} days =&nbsp;
              <span className="car-price-total">{fmtPrice(total)}</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── EXTRAS CARD ─────────────────────────────────────────────
function ExtrasCard({ extras, days, onToggle, loading }) {
  if (loading) return (
    <div className="s-card">
      <div className="s-title">Optional Extras</div>
      {[...Array(4)].map((_, i) => (
        <div key={i} style={{ height:50, marginBottom:8, borderRadius:8 }} className="skeleton" />
      ))}
    </div>
  );

  return (
    <div className="s-card">
      <div className="s-title">Optional Extras</div>
      {extras.map(ex => {
        const lineTotal = ex.isFree ? 0 : ex.unit === "per day" ? ex.price * days : ex.price;
        return (
          <div key={ex.id} className="extra-row" onClick={() => onToggle(ex.id)}>
            <span className="extra-icon">{ex.icon}</span>
            <div>
              <div className="extra-name">{ex.name}</div>
              <div className="extra-desc">{ex.desc}</div>
            </div>
            <div className="extra-price">
              {ex.isFree
                ? <span className="extra-free">Free</span>
                : fmtPrice(lineTotal)
              }
              {!ex.isFree && (
                <small>
                  {ex.unit === "per day" ? `${fmtPrice(ex.price)}/day × ${days}` : "flat rate"}
                </small>
              )}
            </div>
            <button
              className={`toggle-btn ${ex.selected ? "on" : "off"}`}
              onClick={e => { e.stopPropagation(); onToggle(ex.id); }}
            />
          </div>
        );
      })}
    </div>
  );
}

// ─── INSURANCE CARD ───────────────────────────────────────────
// function InsuranceCard({ insuranceList, daySlabs, days, selectedIns, onSelect, loading }) {
//   if (loading) return (
//     <div className="s-card">
//       <div className="s-title">Insurance</div>
//       {[...Array(3)].map((_, i) => (
//         <div key={i} style={{ height:50, marginBottom:8, borderRadius:8 }} className="skeleton" />
//       ))}
//     </div>
//   );

//   return (
//     <div className="s-card">
//       <div className="s-title">Insurance</div>

//       <div className="ins-row" onClick={() => onSelect(null)}>
//         <span className="extra-icon">🚫</span>
//         <div>
//           <div className="extra-name">No Insurance</div>
//           <div className="extra-desc">I accept the risk (not recommended)</div>
//         </div>
//         <div className="ins-price" style={{ color:"var(--muted)" }}>—</div>
//         <div className={`ins-radio ${!selectedIns ? "on" : ""}`} />
//       </div>

//       {insuranceList.map(ins => {
//         const price  = resolveInsurancePrice(ins, days, daySlabs);
//         const chosen = selectedIns?.ins_id === ins.ins_id;
//         return (
//           <div key={ins.ins_id} className="ins-row" onClick={() => onSelect(ins)}>
//             <span className="extra-icon">🛡️</span>
//             <div>
//               <div className="extra-name">{ins.ins_item}</div>
//               <div className="extra-desc">
//                 {daySlabs.length
//                   ? `Slab rate for ${days} day${days > 1 ? "s" : ""}`
//                   : `${fmtPrice(ins.ins_chargeperday)}/day`}
//               </div>
//             </div>
//             <div className="ins-price">
//               {fmtPrice(price)}
//               <small>total</small>
//             </div>
//             <div className={`ins-radio ${chosen ? "on" : ""}`} />
//           </div>
//         );
//       })}
//     </div>
//   );
// }
function InsuranceCard({ insuranceList, daySlabs, days, selectedIns, onToggle, loading }) {
  if (loading) return (
    <div className="s-card">
      <div className="s-title">Insurance</div>
      {[...Array(3)].map((_, i) => (
        <div key={i} style={{ height:50, marginBottom:8, borderRadius:8 }} className="skeleton" />
      ))}
    </div>
  );

  return (
    <div className="s-card">
      <div className="s-title">Insurance</div>
      {insuranceList.length === 0 && (
        <div style={{ fontSize:".8rem", color:"var(--muted)", textAlign:"center", padding:".5rem 0" }}>
          No insurance options available
        </div>
      )}
      {insuranceList.map(ins => {
        const price    = resolveInsurancePrice(ins, days, daySlabs);
        const isChosen = (selectedIns || []).some(s => s.ins_id === ins.ins_id);
        return (
          <div key={ins.ins_id} className="extra-row" onClick={() => onToggle(ins)}>
            <span className="extra-icon">🛡️</span>
            <div>
              <div className="extra-name">{ins.ins_item}</div>
              <div className="extra-desc">
                {daySlabs.length
                  ? `Slab rate for ${days} day${days > 1 ? "s" : ""}`
                  : `${fmtPrice(ins.ins_chargeperday)}/day`}
              </div>
            </div>
            <div className="extra-price">
              {fmtPrice(price)}
              <small>total</small>
            </div>
            <button
              className={`toggle-btn ${isChosen ? "on" : "off"}`}
              onClick={e => { e.stopPropagation(); onToggle(ins); }}
            />
          </div>
        );
      })}
    </div>
  );
}


// ─── PERSONAL INFO FORM ───────────────────────────────────────
// ─── TOAST MANAGER HOOK ────────────────────────────────────────


let _toastId = 0;

export function useToast() {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts(ts =>
      ts.map(t => t.id === id ? { ...t, removing: true } : t)
    );
    setTimeout(() => setToasts(ts => ts.filter(t => t.id !== id)), 260);
  }, []);

  const push = useCallback(({ type = "info", title, message, duration = 5000 }) => {
    const id = ++_toastId;
    setToasts(ts => [...ts, { id, type, title, message, removing: false }]);
    if (duration > 0) setTimeout(() => dismiss(id), duration);
    return id;
  }, [dismiss]);

  const ToastContainer = useCallback(() => (
    <div className="toast-container">
      {toasts.map(t => {
        const icons = { error: "🚫", success: "✅", warning: "⚠️", info: "ℹ️" };
        return (
          <div key={t.id} className={`toast toast-${t.type} ${t.removing ? "removing" : ""}`}>
            <span className="toast-icon">{icons[t.type]}</span>
            <div className="toast-body">
              {t.title && <div className="toast-title">{t.title}</div>}
              <div className="toast-msg">{t.message}</div>
            </div>
            <button className="toast-close" onClick={() => dismiss(t.id)}>✕</button>
          </div>
        );
      })}
    </div>
  ), [toasts, dismiss]);

  return { push, ToastContainer };
}

// ─── ENHANCED PersonalInfoForm ────────────────────────────────
// const COUNTRIES = [
//   "India (+91)", "UK (+44)", "USA (+1)", "Australia (+61)",
//   "Canada (+1)", "UAE (+971)", "Germany (+49)", "France (+33)",
//   "Singapore (+65)", "Japan (+81)", "New Zealand (+64)", "South Africa (+27)",
// ];

export function PersonalInfoForm({ form, onChange, termsAccepted, onTermsChange, onValidate }) {
  const [prevBooking, setPrevBooking]   = useState(false);
  const [prefillRef,  setPrefillRef]    = useState("");
  const [prefillState, setPrefillState] = useState("idle"); // idle | loading | found | notfound
  const [touched, setTouched]           = useState({});
  const [fieldErrors, setFieldErrors]   = useState({});
  const [showSummary, setShowSummary]   = useState(false);
  const { push, ToastContainer }        = useToast();

  // Validate a single field on blur
  const handleBlur = useCallback((name) => {
    setTouched(t => ({ ...t, [name]: true }));
    const err = validateField(name, form[name], form);
    setFieldErrors(e => ({ ...e, [name]: err }));
  }, [form]);

  // Live re-validate confirm email when email changes
  useEffect(() => {
    if (touched["Confirm Email"]) {
      const err = validateField("Confirm Email", form["Confirm Email"], form);
      setFieldErrors(e => ({ ...e, "Confirm Email": err }));
    }
  }, [form["Email"], form["Confirm Email"]]);

  // Expose validate() so parent (proceed button) can trigger full validation
  useEffect(() => {
    if (onValidate) {
      onValidate(() => {
        const all = Object.keys(VALIDATORS);
        const allTouched = {};
        all.forEach(k => (allTouched[k] = true));
        setTouched(allTouched);

        const { errors, firstError, isValid } = validateAllFields(form);
        setFieldErrors(errors);

        if (!isValid) {
          setShowSummary(true);
          // Toast summary
          const count = Object.keys(errors).length;
          push({
            type: "error",
            title: `${count} field${count > 1 ? "s" : ""} need attention`,
            message: `Please fix: ${Object.values(errors).slice(0, 2).join(" · ")}${count > 2 ? ` + ${count - 2} more` : ""}`,
            duration: 7000,
          });
          // Scroll to first error field
          if (firstError) {
            const el = document.querySelector(`[data-field="${firstError.name}"]`);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        } else {
          setShowSummary(false);
          push({ type: "success", title: "All good!", message: "Details verified — proceeding to payment.", duration: 3000 });
        }
        return isValid;
      });
    }
  }, [onValidate, form]);

  const handleChange = (name, value) => {
    onChange(name, value);
    if (touched[name]) {
      const err = validateField(name, value, { ...form, [name]: value });
      setFieldErrors(e => ({ ...e, [name]: err }));
      if (!err && showSummary) {
        const remaining = { ...fieldErrors, [name]: null };
        if (Object.values(remaining).every(v => !v)) setShowSummary(false);
      }
    }
  };

  // ── Field component
  const renderField = ({ name, placeholder, type = "text", extra = {} }) => {
    const err = fieldErrors[name];
    const ok  = touched[name] && !err && (form[name] || "").trim().length > 0;
    return (
      <div className="form-group" data-field={name} key={name}>
        <label className="form-label">{name}</label>
        <input
          className={`form-input ${err ? "field-error" : ok ? "field-ok" : ""}`}
          type={type}
          placeholder={placeholder}
          value={form[name] || ""}
          onChange={e => handleChange(name, e.target.value)}
          onBlur={() => handleBlur(name)}
          aria-invalid={!!err}
          aria-describedby={err ? `err-${name}` : undefined}
          {...extra}
        />
        {err && <div className="field-error-msg" id={`err-${name}`} role="alert">{err}</div>}
      </div>
    );
  };

  // ── Select component
  const renderSelect = ({ name, options, value, onChangeFn }) => {
    const err = fieldErrors[name];
    const ok  = touched[name] && !err;
    return (
      <div className="form-group" data-field={name} key={name}>
        <label className="form-label">{name}</label>
        <select
          className={`form-select ${err ? "field-error" : ok ? "field-ok" : ""}`}
          value={value}
          onChange={e => onChangeFn(e.target.value)}
          onBlur={() => handleBlur(name)}
        >
          {options.map(o => <option key={o}>{o}</option>)}
        </select>
        {err && <div className="field-error-msg" role="alert" style={{color:'red'}}>{err}</div>}
      </div>
    );
  };

  const handlePrefill = async () => {
    const ref = prefillRef.trim();
    if (!ref) return;
    setPrefillState("loading");
    try {
      // const res = await fetch(`${API_BASE}/online-bookings/prefill/${encodeURIComponent(ref)}`);
      // const res = await fetch(`${API_BASE}/booking-p/prefill/${encodeURIComponent(ref)}`);
      // if (!res.ok) throw new Error("not found");
      // const d = await res.json();
      const res = await API.get(`/booking-p/prefill/${encodeURIComponent(ref)}`);
      const d = res.data;

      const mapped = {
        title:           d.online_title     || "Mr",
        "First Name":    d.online_firstname || "",
        "Last Name":     d.online_lastname  || "",
        "Mobile No":     d.online_mobileno  || "",
        Email:           d.online_emailid   || "",
        "Confirm Email": d.online_emailid   || "",
        Address:         d.online_address   || "",
        City:            d.online_city      || "",
        "Post Code":     d.online_postcode  || "",
        country:         d.online_country   || "India (+91)",
        "DL Number":     d.dl_number  || "",
        "DL Expiry":     d.dl_expirydate
          ? new Date(d.dl_expirydate).toISOString().split("T")[0]
          : "",
        comments:        d.online_comments  || "",
        prevBookingNum:  ref,
      };
      Object.entries(mapped).forEach(([key, val]) => onChange(key, val));

      setPrefillState("found");
      push({
        type: "success",
        title: "Details loaded!",
        message: `Booking #${ref} details prefilled. Please review before proceeding.`,
        duration: 5000,
      });
    } catch {
      setPrefillState("notfound");
      push({
        type: "error",
        title: "Booking not found",
        message: `No booking found with ref #${ref}. Please check and try again.`,
        duration: 5000,
      });
    }
  };

  const errCount = Object.values(fieldErrors).filter(Boolean).length;

  return (
    <>
    <style>{validationCSS}</style>
      <ToastContainer />

      <div className="s-card">
        <div className="s-title">Personal Information</div>

        {/* ── Summary error banner ── */}
        {showSummary && errCount > 0 && (
          <div className="validation-summary" role="alert">
            <span className="val-sum-icon">🚫</span>
            <div>
              <strong>{errCount} issue{errCount > 1 ? "s" : ""} found</strong>
              {Object.entries(fieldErrors)
                .filter(([, v]) => v)
                .map(([k, v]) => (
                  <div key={k} style={{ marginBottom: ".15rem" }}>
                    <span style={{ color: "#E24B4A", fontWeight: 600 }}>{k}:</span>{" "}
                    {v}
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* ── Previous booking ── */}
        {/* ── Previous booking ── */}
        <div className="prev-booking-bar">
          <label className="prev-chk-label">
            <input
              type="checkbox"
              checked={prevBooking}
              onChange={e => {
                setPrevBooking(e.target.checked);
                if (!e.target.checked) {
                  setPrefillRef("");
                  setPrefillState("idle");
                }
              }}
            />
            Previous booking?
          </label>
          <input
            className="prev-num-input"
            type="text"
            placeholder="Enter booking number…"
            disabled={!prevBooking}
            value={prefillRef}
            onChange={e => { setPrefillRef(e.target.value); setPrefillState("idle"); }}
            onKeyDown={e => e.key === "Enter" && prevBooking && handlePrefill()}
          />
          {prevBooking && (
            <button
              onClick={handlePrefill}
              disabled={!prefillRef.trim() || prefillState === "loading"}
              style={{
                background: prefillState === "found" ? "var(--green)" : "var(--gold)",
                color: "var(--dark)", border: "none", borderRadius: "8px",
                padding: ".3rem .85rem", fontWeight: 700, fontSize: ".78rem",
                cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap",
                opacity: !prefillRef.trim() ? 0.5 : 1,
                transition: "all .2s",
              }}
            >
              {prefillState === "loading" ? "⏳ Loading…"
                : prefillState === "found" ? "✓ Loaded"
                : "Auto-fill"}
            </button>
          )}
        </div>

        {/* ── Title + First Name ── */}
        <div className="form-grid">
          {renderSelect({ name:"Title", options:["Mr","Ms","Mrs","Dr"], value:form.title||"Mr", onChangeFn:v=>handleChange("title",v) })}
          {renderField({ name:"First Name", placeholder:"First name" })}
        </div>

        {/* ── Last Name + Mobile ── */}
        <div className="form-grid">
          {renderField({ name:"Last Name", placeholder:"Last name" })}
          {renderField({ name:"Mobile No", placeholder:"+91 98765 43210", type:"tel" })}
        </div>

        {/* ── Email + Confirm ── */}
        <div className="form-grid">
          {renderField({ name:"Email", placeholder:"you@email.com", type:"email" })}
          {renderField({ name:"Confirm Email", placeholder:"Re-enter email", type:"email" })}
        </div>

        {/* ── Address + City ── */}
        <div className="form-grid">
        {renderField({ name:"Address", placeholder:"Street address" })}
        {renderField({ name:"City", placeholder:"City" })}
        </div>

        {/* ── Post Code + Country ── */}
        <div className="form-grid">
        {renderField({ name:"Post Code", placeholder:"PIN code" })}
        {renderSelect({ name:"Country", options:COUNTRIES, value:form.country||"India (+91)", onChangeFn:v=>handleChange("country",v) })}
        </div>

        {/* ── DL Number + DL Expiry ── */}
        <div className="form-grid">
        {renderField({ name:"DL Number", placeholder:"Driving licence number" })}
        {renderField({ name:"DL Expiry", placeholder:"", type:"date" })}
        </div>

        {/* ── Comments ── */}
        <div className="form-grid full">
          <div className="form-group">
            <label className="form-label">Pickup / Drop-off Details</label>
            <textarea
              className="form-textarea"
              placeholder="Any specific instructions for pickup or drop-off…"
              value={form.comments || ""}
              onChange={e => handleChange("comments", e.target.value)}
            />
          </div>
        </div>

        {/* ── Terms ── */}
        <div className="terms-check">
          <input
            type="checkbox"
            id="termsChk"
            checked={termsAccepted}
            onChange={e => {
              onTermsChange(e.target.checked);
              if (!e.target.checked) {
                push({ type: "warning", title: "Terms required", message: "You must accept the Terms & Conditions to proceed.", duration: 4000 });
              }
            }}
          />
          <label htmlFor="termsChk">
            I confirm that I have read and agree to the{" "}
            <span className="terms-link">Terms &amp; Conditions</span>.
          </label>
        </div>
      </div>
    </>
  );
}

// ─── RIGHT PANEL ─────────────────────────────────────────────
function SummaryPanel({ ctx, car, extras, daySlabs, selectedIns, termsAccepted, submitting, onProceed, onPayLater }) {
  const days        = ctx.days || 1;
  const baseTotal   = (car?.base_price || 0) * days;
  const nightPickup = isNightTime(ctx.pickupTime);
  const nightReturn = isNightTime(ctx.returnTime);
  const nightTotal  = (nightPickup ? NIGHT_CHARGE : 0) + (nightReturn ? NIGHT_CHARGE : 0);

  const extrasTotal = extras
    .filter(e => e.selected && !e.isFree)
    .reduce((s, e) => s + (e.unit === "per day" ? e.price * days : e.price), 0);

  // const insPrice = selectedIns ? resolveInsurancePrice(selectedIns, days, daySlabs) : 0;
  const insPrice = totalInsurancePrice(selectedIns, days, daySlabs);
  const subTotal = baseTotal + nightTotal + extrasTotal + insPrice;
  const tax      = subTotal * TAX_RATE;
  const grand    = subTotal + tax;
  const advance  = grand * ADV_PCT;

  const canProceed = termsAccepted && !!car && !submitting;

  return (
    <aside className="right-col">

      {(nightPickup || nightReturn) && (
        <div className="night-notice">
          <span>🌙</span>
          <span>
            Night surcharge for {nightPickup && "pickup"}{nightPickup && nightReturn && " & "}{nightReturn && "return"}.
            <strong style={{ display:"block" }}>+{fmtPrice(NIGHT_CHARGE)} per instance</strong>
          </span>
        </div>
      )}

      <div className="bp-section">
        <div className="bp-heading">Price Summary</div>

        {car && (
          <div className="price-row">
            <span className="price-label">{fmtPrice(car.base_price)} × {days} days</span>
            <span className="price-val">{fmtPrice(baseTotal)}</span>
          </div>
        )}
        {nightTotal > 0 && (
          <div className="price-row">
            <span className="price-label">🌙 Night charges</span>
            <span className="price-val">{fmtPrice(nightTotal)}</span>
          </div>
        )}
        {extras.filter(e => e.selected).map(e => {
          const ln = e.isFree ? 0 : e.unit === "per day" ? e.price * days : e.price;
          return (
            <div key={e.id} className="price-row">
              <span className="price-label">{e.icon} {e.name}</span>
              <span className="price-val">
                {e.isFree ? <span style={{ color:"var(--green)" }}>Free</span> : fmtPrice(ln)}
              </span>
            </div>
          );
        })}
        {/* {selectedIns && (
          <div className="price-row">
            <span className="price-label">🛡️ {selectedIns.ins_item}</span>
            <span className="price-val">{fmtPrice(insPrice)}</span>
          </div>
        )} */}
        {(selectedIns || []).map(ins => (
        <div key={ins.ins_id} className="price-row">
        <span className="price-label">🛡️ {ins.ins_item}</span>
        <span className="price-val">{fmtPrice(resolveInsurancePrice(ins, days, daySlabs))}</span>
        </div>
        ))}
        {!car && <div style={{ fontSize:".8rem", color:"var(--muted)", textAlign:"center", padding:".5rem 0" }}>No vehicle selected</div>}

        <div className="price-row sub-total">
          <span className="price-label">Sub-total</span>
          <span className="price-val">{fmtPrice(subTotal)}</span>
        </div>
        <div className="price-row">
          <span className="price-label" style={{ fontSize:".76rem" }}>Tax (18%)</span>
          <span className="price-val"   style={{ fontSize:".8rem"  }}>{fmtPrice(tax)}</span>
        </div>
        <div className="price-row grand">
          <span className="price-label">Total</span>
          <span className="price-val">{fmtPrice(grand)}</span>
        </div>
      </div>

      <div className="adv-box">
        <div className="adv-label">Advance to Pay Now</div>
        <div className="adv-amount">{fmtPrice(advance)}</div>
        <div className="adv-sub">30% of total · balance at pickup</div>
      </div>

      <div className="bp-section">
        <div className="bp-heading">Rental Details</div>
        <div className="price-row"><span className="price-label">Daily Rate</span>   <span className="price-val">{fmtPrice(car?.base_price)}</span></div>
        <div className="price-row"><span className="price-label">Rental Days</span>  <span className="price-val">{days} day{days > 1 ? "s" : ""}</span></div>
        <div className="price-row"><span className="price-label">Pickup</span>       <span className="price-val">{ctx.pickupDate} {ctx.pickupTime}</span></div>
        <div className="price-row"><span className="price-label">Return</span>       <span className="price-val">{ctx.returnDate} {ctx.returnTime}</span></div>
        <div className="price-row"><span className="price-label">Location</span>     <span className="price-val">{ctx.pickup}</span></div>
        {ctx.driverAge && <div className="price-row"><span className="price-label">Driver Age</span><span className="price-val">{ctx.driverAge}</span></div>}
      </div>

      {/* CTA STACK — also rendered fixed on mobile via CSS */}
      <div className="cta-stack">
        <div className="paylater-info">
          <span>🕐</span>
          <span>
            <strong style={{ color:"var(--amber)" }}>Pay Later</strong> — confirm now with ₹0 advance. Full payment due at pickup.
          </span>
        </div>

        <button className="btn-proceed" disabled={!canProceed} onClick={onProceed}>
          {!car
            ? "No Vehicle Selected"
            : !termsAccepted
              ? "Accept Terms to Continue"
              : submitting
                ? "Processing…"
                : <><span>Proceed to Payment</span><span>→</span></>
          }
        </button>

        <button className="btn-pay-later" disabled={!canProceed} onClick={onPayLater}>
          <span>Reserve, Pay at Pickup</span>
          <span className="pay-later-badge">PAY LATER</span>
        </button>

        <div className="secure-note">🔒 Secure · Free cancellation · No hidden fees</div>
      </div>
    </aside>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────
export default function BookingStep3() {
  const navigate = useNavigate();
  const location = useLocation();
  const validateRef = useRef(null);
  console.log(location.state);
  const ctx = useMemo(() => {
    const s = location.state;
    return s || mockBookingContext;
  }, [location.state]);

  const { data: rawExtras,    loading: extLoading } = useFetch("/optional-extras", mockExtras);
  const { data: rawInsurance, loading: insLoading } = useFetch("/insurance",        mockInsurance);
  const { data: rawDaySlabs                       } = useFetch("/insurance/day-slabs", []);

  const [extras, setExtras] = useState(() => {
    const base = mapExtras(mockExtras);
    const pre  = ctx.selectedExtras?.map(e => e.id) ?? [];
    return base.map(e => ({ ...e, selected: pre.includes(e.id) }));
  });

  useEffect(() => {
    if (rawExtras) {
      const pre = ctx.selectedExtras?.map(e => e.id) ?? [];
      setExtras(mapExtras(rawExtras).map(e => ({ ...e, selected: pre.includes(e.id) })));
    }
  }, [rawExtras]);

  // const [selectedIns,   setSelectedIns  ] = useState(ctx.selectedInsurance ?? null);

const [selectedIns, setSelectedIns] = useState(
  Array.isArray(ctx.selectedInsurance)
    ? ctx.selectedInsurance
    : ctx.selectedInsurance
      ? [ctx.selectedInsurance]
      : []
);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting,    setSubmitting   ] = useState(false);
  const [form, setForm] = useState({
    title:"Mr", "First Name":"", "Last Name":"",
    "Mobile No":"", Email:"", "Confirm Email":"",
    Address:"", City:"", "Post Code":"",
    country:"India (+91)", "DL Number":"", "DL Expiry":"",
    comments:"", prevBookingNum:"",
  });

  const formatDateForInput = (isoDate) => {
    if (!isoDate) return "";
  
    const d = new Date(isoDate);
  
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
  
    return `${year}-${month}-${day}`;
  };
  
  useEffect(() => {
    if (location.state?.customer) {
      const c = location.state.customer;
  
      setForm(prev => ({
        ...prev,
        title: c.title || "Mr",
        "First Name": c.firstName || "",
        "Last Name": c.lastName || "",
        "Mobile No": c.mobile || "",
        Email: c.email || "",
        "Confirm Email": c.email || "",
        Address: c.address || "",
        "Post Code": c.postcode || "",
        City: c.city || "",
        country: c.country || "India (+91)",
        "DL Number": c.dlnumber|| "",
        "DL Expiry": formatDateForInput(c.dlexpirydt),
        comments: c.comments|| "",
      }));
    }
  }, [location.state]);

  const insuranceList = mapInsurance(rawInsurance);
  const daySlabs = Array.isArray(rawDaySlabs?.data) ? rawDaySlabs.data
    : Array.isArray(rawDaySlabs) ? rawDaySlabs : [];

  const toggleExtra  = id => setExtras(ex => ex.map(e => e.id === id ? { ...e, selected: !e.selected } : e));
  const updateForm   = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const buildPayload = (payLater = false) => {
    const days      = ctx.days || 1;
    const car       = ctx.selectedCar;
    const baseTotal = (car?.base_price || 0) * days;
    const nightPickup = isNightTime(ctx.pickupTime);
    const nightReturn = isNightTime(ctx.returnTime);
    const nightTotal  = (nightPickup ? NIGHT_CHARGE : 0) + (nightReturn ? NIGHT_CHARGE : 0);
    const extrasTotal = extras
      .filter(e => e.selected && !e.isFree)
      .reduce((s, e) => s + (e.unit === "per day" ? e.price * days : e.price), 0);
    // const insPrice  = selectedIns ? resolveInsurancePrice(selectedIns, days, daySlabs) : 0;
    const insPrice  = totalInsurancePrice(selectedIns, days, daySlabs);
    const subTotal  = baseTotal + nightTotal + extrasTotal + insPrice;
    const tax       = Math.round(subTotal * TAX_RATE);
    const grand     = subTotal + tax;
    const advance   = payLater ? 0 : Math.round(grand * ADV_PCT);
    
    return {
      title:form.title, firstName:form["First Name"], lastName:form["Last Name"],
      mobile:form["Mobile No"], email:form.Email,
      address:form.Address, city:form.City, postCode:form["Post Code"],
      country:form.country, dlNumber:form["DL Number"], dlExpiry:form["DL Expiry"],
      comments:form.comments, prevBookingNum:form.prevBookingNum || "",
      pickup:ctx.pickup, dropoff:ctx.dropoff||ctx.pickup,
      pickupDate:ctx.pickupDate, pickupTime:ctx.pickupTime,
      returnDate:ctx.returnDate, returnTime:ctx.returnTime,
      days, driverAge:ctx.driverAge,
      carId:ctx.selectedCar?.car_id, carName:ctx.selectedCar?.car_name,
      groupId:ctx.selectedCar?.group_name, onlinePrice:ctx.selectedCar?.base_price,
      selectedExtras:extras.filter(e=>e.selected).map(e=>({id:e.id,name:e.name,price:e.price,unit:e.unit,isFree:e.isFree})),
      // insuranceId:selectedIns?.ins_id??null, insuranceName:selectedIns?.ins_item??null,
      insuranceIds:(selectedIns||[]).map(i=>i.ins_id),
      insuranceNames:(selectedIns||[]).map(i=>i.ins_item),
      extrasTotal, insTotal:insPrice, nightCharges:nightTotal,
      localTax:TAX_RATE*100, taxAmount:tax, totalAmount:grand, advanceAmount:advance,
      payLater,
    };
  };

  // const handleProceed = () => {
  //   navigate("/booking-step4", {
  //     state: {
  //       ...ctx,
  //       selectedExtras:    extras.filter(e => e.selected),
  //       // selectedInsurance: selectedIns,
  //       selectedInsurance: selectedIns || [],
  //       personalInfo:      form,
  //     },
  //   });
  // };
  const handleProceed = async () => {
           if (validateRef.current) {
             const ok = await validateRef.current();
             if (!ok) return;
           }
           navigate("/booking-step4", { state: {
            ...ctx,
            selectedExtras:    extras.filter(e => e.selected),
            // selectedInsurance: selectedIns,
            selectedInsurance: selectedIns || [],
            personalInfo:      form,
          },
         });
         };


  const handlePayLater = async () => {
    if (validateRef.current) {
               const ok = await validateRef.current();
               if (!ok) return;
             }
    setSubmitting(true);
    try {
      
      const payload = buildPayload(true);
      // const res = await fetch(`${API_BASE}/booking/submit`, {
      //   method:"POST", headers:{"Content-Type":"application/json"},
      //   body:JSON.stringify(payload),
      // });
      // if (!res.ok) {
      //   const err = await res.json().catch(() => ({ message:"Server error" }));
      //   throw new Error(err.message || `HTTP ${res.status}`);
      // }
      // const { bookingId } = await res.json();
      const res = await API.post("/booking/submit", payload);
      const { bookingId } = res.data;
      // fetch(`${API_BASE}/email/booking-confirmation`, {
      //   method:"POST", headers:{"Content-Type":"application/json"},
      //   body:JSON.stringify({ bookingId, payLater:true, ...payload }),
      // }).catch(err => console.warn("Email failed:", err));
      API.post("/email/booking-confirmation", { bookingId, payLater: true, ...payload })
  .catch(err => console.warn("Email failed:", err));
      navigate(`/confirmation/${bookingId}`);
    } catch (err) {
      console.error("Pay Later failed:", err);
      alert(`Booking failed: ${err.message}\n\nPlease try again or contact support.`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <style>{css}</style>

      {submitting && (
        <div className="submitting-overlay">
          <div className="spinner" />
          <div className="submitting-text">Confirming your reservation…</div>
        </div>
      )}

      <StepNav siteName="-Fleet" onLogoClick={() => navigate("/")} />

      <div className="step3-wrap">
        <SummaryBar ctx={ctx} onEdit={() => navigate("/home", { state: ctx })} />

        <div className="step3-header">
          <h1>Reserve Your Vehicle</h1>
          <p>Review your selection, choose extras, and fill in your details to complete the reservation.</p>
        </div>

        <div className="step3-layout">
          <div className="left-col">
            <CarSummaryCard car={ctx.selectedCar} days={ctx.days} />
            <ExtrasCard extras={extras} days={ctx.days} onToggle={toggleExtra} loading={extLoading} />
            {/* <InsuranceCard
              insuranceList={insuranceList} daySlabs={daySlabs} days={ctx.days}
              selectedIns={selectedIns} onSelect={setSelectedIns} loading={insLoading}
            /> */}
            <InsuranceCard
            insuranceList={insuranceList} daySlabs={daySlabs} days={ctx.days}
            selectedIns={selectedIns}
            onToggle={(ins) => {
            setSelectedIns(prev => {
            const already = (prev || []).some(s => s.ins_id === ins.ins_id);
            return already
            ? (prev || []).filter(s => s.ins_id !== ins.ins_id)
            : [...(prev || []), ins];
            });
            }}
            loading={insLoading}
            />

            {/* <PersonalInfoForm
              form={form} onChange={updateForm}
              termsAccepted={termsAccepted} onTermsChange={setTermsAccepted}
            /> */}
            <PersonalInfoForm
            form={form} onChange={updateForm}
            termsAccepted={termsAccepted} onTermsChange={setTermsAccepted}
            onValidate={fn => { validateRef.current = fn; }}
          />
          </div>

          <SummaryPanel
            ctx={ctx} car={ctx.selectedCar}
            extras={extras} daySlabs={daySlabs} selectedIns={selectedIns}
            termsAccepted={termsAccepted} submitting={submitting}
            onProceed={handleProceed} onPayLater={handlePayLater}
          />
        </div>
      </div>
    </>
  );
}